import React, { useRef, useState, useEffect, useLayoutEffect, useMemo } from "react";
import { DateRange } from "react-date-range";
import { format } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Form, Button } from "../../../evo/bootstrap";

const DateRangeSelector = ({ onSelect, value, small = false, maxDate = null }: any) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<any>(null);
  const dropdownRef = useRef<any>(null);
  const [position, setPosition] = useState<any>(null);

  const [range, setRange] = useState(value);  // Use prop as initial
  
  // Set maxDate to today if not provided (disable future dates)
  // Memoize to avoid recalculating on every render
  const maxSelectableDate = useMemo(() => {
    if (maxDate) return maxDate;
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    return today;
  }, [maxDate]);

  // Sync local state when parent value changes
  useEffect(() => {
    if (value && value[0]) {
      let startDate = value[0].startDate;
      let endDate = value[0].endDate;
      
      // Cap dates to maxSelectableDate if they exceed it
      if (startDate > maxSelectableDate) {
        startDate = new Date(maxSelectableDate);
      }
      if (endDate > maxSelectableDate) {
        endDate = new Date(maxSelectableDate);
      }
      
      // Ensure startDate is not after endDate
      if (startDate > endDate) {
        endDate = new Date(startDate);
      }
      
      setRange([{
        ...value[0],
        startDate,
        endDate
      }]);
    } else {
      setRange(value);
    }
  }, [value, maxSelectableDate]);

  // Calculate position when opening - use useLayoutEffect to prevent visible transition
  useLayoutEffect(() => {
    if (open && ref.current) {
      // Always use relative positioning (top: 100%) for better scrolling behavior
      // This allows the date picker to scroll with the modal content
      setPosition({
        top: '100%',
        left: 0,
      });
    } else if (!open) {
      // Reset position when closed
      setPosition(null);
    }
  }, [open]);

  const handleSelect = (ranges) => {
    const selection = ranges.selection;
    
    // Cap dates to maxSelectableDate (today) if they exceed it
    let startDate = selection.startDate;
    let endDate = selection.endDate;
    
    if (startDate > maxSelectableDate) {
      startDate = new Date(maxSelectableDate);
    }
    if (endDate > maxSelectableDate) {
      endDate = new Date(maxSelectableDate);
    }
    
    // Ensure startDate is not after endDate
    if (startDate > endDate) {
      endDate = new Date(startDate);
    }
    
    const cappedSelection = {
      ...selection,
      startDate,
      endDate
    };
    
    setRange([cappedSelection]);
    onSelect({
      from_date: format(startDate, "yyyy-MM-dd"),
      to_date: format(endDate, "yyyy-MM-dd"),
    });
  };

  const handleClear = () => {
    const today = new Date();
    const reset = {
      startDate: today,
      endDate: today,
      key: "selection",
    };
    setRange([reset]);
    onSelect({ from_date: "", to_date: "" });
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="position-relative date-range-selector-wrapper" ref={ref}>
      <Form.Label className="fw-semibold">Date range</Form.Label>
      <input
        type="text"
        readOnly
        className="form-control date-range-input"
        value={`${format(range[0].startDate, "MMM d, yyyy")} - ${format(
          range[0].endDate,
          "MMM d, yyyy"
        )}`}
        onClick={() => setOpen(!open)}
        style={{ cursor: 'pointer' }}
      />

      {open && position && (
        <div
          ref={dropdownRef}
          className="date-range-picker-dropdown shadow bg-white pb-2"
          style={{ 
            zIndex: 9999,
            position: 'absolute',
            top: typeof position.top === 'number' ? `${position.top}px` : position.top,
            left: typeof position.left === 'number' ? `${position.left}px` : position.left,
          }}
        >
          <div className="date-range-calendar-wrapper">
            <DateRange
              ranges={range}
              onChange={handleSelect}
              months={small ? 1 : 2}
              direction="horizontal"
              dragSelectionEnabled={true}
              editableDateInputs={false}
              shownDate={range[0].startDate}
              maxDate={maxSelectableDate}
              className="custom-date-range"
            />
          </div>

          <div className="text-end mt-2 justify-content-end px-2 d-flex gap-2">
            <Button 
              variant="secondary" 
              size="sm" 
              className="date-picker-clear-btn"
              onClick={handleClear}
            >
              Clear
            </Button>
            <Button 
              variant="primary" 
              size="sm" 
              className="date-picker-done-btn me-2"
              onClick={() => setOpen(false)}
            >
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;
